import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, TrendingUp, Users, Building, Star, CheckCircle, Mail, Phone, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BulkDiscounts = () => {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState('volume');

  const discountTiers = [
    {
      id: 'volume',
      name: 'Volume Discounts',
      icon: TrendingUp,
      description: 'Automatic discounts based on monthly usage',
      color: 'blue',
      tiers: [
        { range: '100-499 credits/month', discount: '15%', price: '$0.85/credit' },
        { range: '500-999 credits/month', discount: '25%', price: '$0.75/credit' },
        { range: '1000+ credits/month', discount: '35%', price: '$0.65/credit' }
      ]
    },
    {
      id: 'team',
      name: 'Team Discounts',
      icon: Users,
      description: 'Special pricing for teams and multiple users',
      color: 'green',
      tiers: [
        { range: '3-9 team members', discount: '20%', price: '$0.80/credit' },
        { range: '10-24 team members', discount: '30%', price: '$0.70/credit' },
        { range: '25+ team members', discount: '40%', price: '$0.60/credit' }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise Discounts',
      icon: Building,
      description: 'Custom pricing for large organizations',
      color: 'purple',
      tiers: [
        { range: '5000+ credits/month', discount: '45%', price: '$0.55/credit' },
        { range: '10000+ credits/month', discount: '50%', price: '$0.50/credit' },
        { range: 'Custom volume', discount: 'Up to 60%', price: 'Contact us' }
      ]
    }
  ];

  const benefits = [
    'Automatic discount calculation',
    'No minimum commitment required',
    'Retroactive discounts applied monthly',
    'Combine with credit package savings',
    'Priority customer support',
    'Custom integration assistance'
  ];

  const useCases = [
    {
      title: 'Accounting Firms',
      description: 'Process multiple client statements monthly',
      example: '500 credits/month = 25% discount = $375/month (vs $500)',
      icon: Calculator
    },
    {
      title: 'Tax Preparation Services',
      description: 'High-volume processing during tax season',
      example: '1000+ credits in March-April = 35% discount',
      icon: Building
    },
    {
      title: 'Bookkeeping Teams',
      description: 'Multiple bookkeepers serving various clients',
      example: '10 team members = 30% discount on all processing',
      icon: Users
    }
  ];

  const currentTier = discountTiers.find(tier => tier.id === selectedTier);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <TrendingUp className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Bulk Discounts
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            The more you process, the more you save. Automatic discounts that scale with your business.
          </p>
        </div>

        {/* Discount Overview */}
        <Card className="shadow-xl border-2 border-green-200 bg-green-50 mb-12">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-green-800 mb-4">Save Up to 60% with Bulk Processing</h2>
              <p className="text-green-700 text-lg">Our bulk discount program automatically reduces your costs as your usage increases.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 text-center shadow-lg">
                <div className="text-3xl font-bold text-blue-600 mb-2">15-35%</div>
                <div className="text-slate-800 font-semibold mb-1">Volume Discounts</div>
                <div className="text-slate-600 text-sm">Based on monthly usage</div>
              </div>
              <div className="bg-white rounded-lg p-6 text-center shadow-lg">
                <div className="text-3xl font-bold text-green-600 mb-2">20-40%</div>
                <div className="text-slate-800 font-semibold mb-1">Team Discounts</div>
                <div className="text-slate-600 text-sm">For multiple users</div>
              </div>
              <div className="bg-white rounded-lg p-6 text-center shadow-lg">
                <div className="text-3xl font-bold text-purple-600 mb-2">45-60%</div>
                <div className="text-slate-800 font-semibold mb-1">Enterprise Savings</div>
                <div className="text-slate-600 text-sm">Custom volume pricing</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Discount Type Selector */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-1 shadow-lg">
            {discountTiers.map((tier) => {
              const IconComponent = tier.icon;
              return (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                    selectedTier === tier.id
                      ? `bg-${tier.color}-600 text-white`
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <IconComponent className="w-5 h-5" />
                  {tier.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Tier Details */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className={`bg-${currentTier.color}-50 border-b`}>
            <CardTitle className={`flex items-center gap-3 text-${currentTier.color}-800`}>
              <currentTier.icon className="w-6 h-6" />
              {currentTier.name}
            </CardTitle>
            <p className={`text-${currentTier.color}-700`}>{currentTier.description}</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {currentTier.tiers.map((tier, index) => (
                <div key={index} className={`bg-${currentTier.color}-50 border border-${currentTier.color}-200 rounded-lg p-6 text-center`}>
                  <div className={`text-2xl font-bold text-${currentTier.color}-600 mb-2`}>{tier.discount}</div>
                  <div className="text-slate-800 font-semibold mb-2">{tier.range}</div>
                  <div className={`text-${currentTier.color}-700 font-medium`}>{tier.price}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="text-slate-800">How Bulk Discounts Work</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-slate-800 mb-4">Automatic Application</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                    <div>
                      <p className="font-medium text-slate-800">Use Tax Helper Pros normally</p>
                      <p className="text-slate-600 text-sm">Process documents with pay-per-use or credit packages</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                    <div>
                      <p className="font-medium text-slate-800">We track your usage</p>
                      <p className="text-slate-600 text-sm">Monthly totals are calculated automatically</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                    <div>
                      <p className="font-medium text-slate-800">Discounts applied retroactively</p>
                      <p className="text-slate-600 text-sm">Credits or refunds issued at month-end</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 mb-4">Benefits Included</h4>
                <div className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-slate-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Use Cases */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-orange-50 border-b">
            <CardTitle className="text-orange-800">Perfect for High-Volume Users</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {useCases.map((useCase, index) => {
                const IconComponent = useCase.icon;
                return (
                  <div key={index} className="bg-white border border-slate-200 rounded-lg p-6">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-orange-600" />
                    </div>
                    <h4 className="font-semibold text-slate-800 mb-2">{useCase.title}</h4>
                    <p className="text-slate-600 text-sm mb-4">{useCase.description}</p>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                      <p className="text-orange-800 text-sm font-medium">{useCase.example}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Comparison Calculator */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-blue-50 border-b">
            <CardTitle className="text-blue-800">Savings Calculator</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-800">Monthly Usage</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-800">Regular Price</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-800">Bulk Discount</th>
                    <th className="text-center py-3 px-4 font-semibold text-green-800">Your Price</th>
                    <th className="text-center py-3 px-4 font-semibold text-green-800">Monthly Savings</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700 font-medium">100 credits</td>
                    <td className="py-3 px-4 text-center">$100</td>
                    <td className="py-3 px-4 text-center text-blue-600">15%</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">$85</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">$15</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700 font-medium">500 credits</td>
                    <td className="py-3 px-4 text-center">$500</td>
                    <td className="py-3 px-4 text-center text-blue-600">25%</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">$375</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">$125</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700 font-medium">1000 credits</td>
                    <td className="py-3 px-4 text-center">$1,000</td>
                    <td className="py-3 px-4 text-center text-blue-600">35%</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">$650</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">$350</td>
                  </tr>
                  <tr className="bg-green-50">
                    <td className="py-3 px-4 text-slate-700 font-medium">5000+ credits</td>
                    <td className="py-3 px-4 text-center">$5,000+</td>
                    <td className="py-3 px-4 text-center text-blue-600">45-60%</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">$2,750+</td>
                    <td className="py-3 px-4 text-center font-semibold text-green-600">$2,250+</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">
                <strong>Note:</strong> Savings are calculated based on volume discounts. Additional team and enterprise 
                discounts can be combined for even greater savings.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Enterprise Contact */}
        <Card className="shadow-xl border-2 border-purple-200 bg-purple-50 mb-12">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Building className="w-8 h-8 text-purple-600" />
                <h3 className="text-2xl font-bold text-purple-800">Enterprise & Custom Volume Pricing</h3>
              </div>
              <p className="text-purple-700 text-lg">
                Processing thousands of documents monthly? Let's discuss custom pricing that can save you even more.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-6">
                <h4 className="font-semibold text-slate-800 mb-4">Enterprise Benefits</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-slate-700">Up to 60% discount on all processing</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-slate-700">Dedicated account manager</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-slate-700">Priority support and SLA</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-slate-700">Custom API integration</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-slate-700">Flexible billing options</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-6">
                <h4 className="font-semibold text-slate-800 mb-4">Contact Enterprise Sales</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-slate-800">Email</p>
                      <p className="text-purple-600">enterprise@taxhelperpros.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-slate-800">Phone</p>
                      <p className="text-purple-600">Available upon request</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button 
                      onClick={() => window.open('mailto:enterprise@taxhelperpros.com')}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Request Enterprise Pricing
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Get Started */}
        <Card className="shadow-xl border-2 border-blue-200 bg-blue-50">
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Star className="w-8 h-8 text-blue-600" />
              <h3 className="text-2xl font-bold text-blue-800">Start Saving with Bulk Discounts Today</h3>
            </div>
            <p className="text-blue-700 text-lg mb-6">
              No setup required, no minimum commitments. Bulk discounts are automatically applied 
              based on your usage patterns.
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
              >
                Get Started Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/contact')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3"
              >
                Contact Sales
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/credit-packages')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            View Credit Packages
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkDiscounts;